/**
 * Eine Zeile der KIAN-Karte: ein Ticket (anlegen/ändern) mit Zustand, Kurzangaben und dem, was
 * noch fehlt. Offene Fragen mit Auswahl lassen sich direkt hier beantworten.
 */
import { Link } from 'react-router-dom';
import { ActionIcon, Anchor, Box, Checkbox, Group, Loader, Select, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconClockHour4,
  IconCircleDashed,
  IconCircleX,
  IconHelpCircle,
  IconEye,
  IconMinus,
  IconPencil,
} from '@tabler/icons-react';
import type { KianAction, KianActionItem, KianDraftIssue } from '../../../lib/api/domains/kianActions';
import {
  ITEM_STATUS_LABEL,
  queueStateText,
  isActionEditable,
  isItemEditable,
  itemFacts,
  queueStateOf,
  ticketHref,
} from './kianActionUi';

interface Props {
  action: KianAction;
  item: KianActionItem;
  busy?: boolean;
  onToggleInclude?: (item: KianActionItem, include: boolean) => void;
  onEdit?: (item: KianActionItem) => void;
  /** Ticket mit der Änderung ansehen (Vorschau) */
  onPreview?: (item: KianActionItem) => void;
  /** Auswahl zu einer offenen Frage (Kandidat) */
  onResolveIssue?: (item: KianActionItem, issue: KianDraftIssue, candidateId: string) => void;
  compact?: boolean;
}

function StatusIcon({ item }: { item: KianActionItem }) {
  const queue = queueStateOf(item);
  const label = queue === 'SYNCED' ? 'nachgereicht' : queue === 'STUCK' ? 'beim Nachreichen abgelehnt' : ITEM_STATUS_LABEL[item.status];
  const icon = (() => {
    switch (item.status) {
      case 'QUEUED':
        return queue === 'SYNCED' ? (
          <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
        ) : queue === 'STUCK' ? (
          <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
        ) : (
          <IconClockHour4 size={16} color="var(--mantine-color-blue-5)" />
        );
      case 'RUNNING':
        return <Loader size={14} />;
      case 'DONE':
        return <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />;
      case 'FAILED':
        return <IconCircleX size={16} color="var(--mantine-color-red-6)" />;
      case 'UNKNOWN':
        return <IconHelpCircle size={16} color="var(--mantine-color-orange-6)" />;
      case 'SKIPPED':
        return <IconMinus size={16} color="var(--mantine-color-gray-6)" />;
      default:
        return item.ready ? (
          <IconCircleDashed size={16} color="var(--mantine-color-gray-6)" />
        ) : (
          <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />
        );
    }
  })();
  return (
    <Tooltip label={item.status === 'PENDING' && !item.ready ? 'braucht noch eine Entscheidung' : label}>
      <Box style={{ display: 'flex', alignItems: 'center', height: 20, flexShrink: 0 }}>{icon}</Box>
    </Tooltip>
  );
}

export function KianActionItemRow({ action, item, busy, onToggleInclude, onEdit, onPreview, onResolveIssue, compact }: Props) {
  const editable = isActionEditable(action) && isItemEditable(item, action.kind);
  const queue = queueStateOf(item);
  const href = ticketHref(item);
  const facts = itemFacts(action, item);
  const errors = item.issues.filter((i) => i.level === 'error');
  const hints = item.issues.filter((i) => i.level !== 'error');
  const dimmed = !item.include && item.status !== 'DONE';
  // Bei Änderungen beginnt die Bezeichnung schon mit der Nummer („T26-01855 · Betreff") —
  // steht die Nummer davor als Link, nicht zweimal zeigen.
  // Auch gescheiterte Anlagen, deren Ticket schon steht — man will nachsehen, was angekommen ist
  const showNumberLink = (item.status === 'DONE' || item.status === 'QUEUED' || (item.status === 'FAILED' && !!item.ticketId)) && !!href;
  const label =
    showNumberLink && item.ticketNumber && item.label.startsWith(item.ticketNumber)
      ? item.label.slice(item.ticketNumber.length).replace(/^\s*·\s*/, '')
      : item.label;

  return (
    <Group
      gap="xs"
      wrap="nowrap"
      align="flex-start"
      py={6}
      style={{ opacity: dimmed ? 0.55 : 1, borderBottom: '1px solid var(--fb-border, rgba(255,255,255,0.06))' }}
    >
      {onToggleInclude ? (
        <Checkbox
          size="xs"
          mt={3}
          checked={item.include}
          disabled={!editable || busy}
          onChange={(e) => {
            const on = e.currentTarget.checked;
            onToggleInclude(item, on);
          }}
          aria-label={item.include ? 'Nicht ausführen' : 'Ausführen'}
        />
      ) : null}
      <StatusIcon item={item} />
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          {showNumberLink && href ? (
            <Anchor component={Link} to={href} size="sm" fw={600} style={{ flexShrink: 0 }}>
              {item.ticketNumber ?? 'Ticket'}
            </Anchor>
          ) : null}
          {onPreview ? (
            <Tooltip label={action.kind === 'TICKET_CREATE' ? 'Vorschau: so wird das Ticket angelegt' : 'Ticket mit der Änderung ansehen'}>
              <Anchor
                component="button"
                type="button"
                size="sm"
                fw={500}
                c="inherit"
                lineClamp={compact ? 1 : 2}
                ta="left"
                style={{ minWidth: 0 }}
                onClick={() => onPreview(item)}
              >
                {label}
              </Anchor>
            </Tooltip>
          ) : (
            <Text size="sm" fw={500} lineClamp={compact ? 1 : 2} style={{ minWidth: 0 }}>
              {label}
            </Text>
          )}
        </Group>
        {facts.length ? (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {facts.join(' · ')}
          </Text>
        ) : null}
        {item.error && (item.status === 'FAILED' || item.status === 'UNKNOWN' || item.status === 'PENDING') ? (
          <Text size="xs" c={item.status === 'UNKNOWN' ? 'orange' : 'red'}>
            {item.error}
          </Text>
        ) : null}
        {queue ? (
          <Text size="xs" c={queue === 'STUCK' ? 'red' : queue === 'SYNCED' ? 'green' : 'blue'}>
            {queueStateText(item)}
          </Text>
        ) : null}
        {item.status !== 'DONE' &&
          item.status !== 'QUEUED' &&
          errors.map((issue, idx) => (
            <Stack key={`e${idx}`} gap={2}>
              <Text size="xs" c="red">
                {issue.message}
              </Text>
              {editable && issue.candidates?.length && onResolveIssue ? (
                <Select
                  size="xs"
                  placeholder="Auswählen …"
                  data={issue.candidates.map((c) => ({ value: c.id, label: c.hint ? `${c.label} · ${c.hint}` : c.label }))}
                  onChange={(v) => v && onResolveIssue(item, issue, v)}
                  searchable
                  disabled={busy}
                  comboboxProps={{ zIndex: 5400 }}
                  maw={360}
                />
              ) : null}
            </Stack>
          ))}
        {!compact && item.status !== 'DONE' && item.status !== 'QUEUED'
          ? hints.slice(0, 3).map((issue, idx) => (
              <Text key={`h${idx}`} size="xs" c={issue.level === 'warning' ? 'yellow' : 'dimmed'}>
                {issue.message}
              </Text>
            ))
          : null}
        {(item.status === 'DONE' || item.status === 'QUEUED') && item.warnings.length
          ? item.warnings.map((w, idx) => (
              <Text key={`w${idx}`} size="xs" c="yellow">
                {w}
              </Text>
            ))
          : null}
      </Stack>
      {onPreview ? (
        <Tooltip label="Vorschau">
          <ActionIcon variant="subtle" size="sm" onClick={() => onPreview(item)} aria-label="Vorschau der Änderung">
            <IconEye size={14} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {editable && onEdit ? (
        <Tooltip label="Bearbeiten">
          <ActionIcon variant="subtle" size="sm" onClick={() => onEdit(item)} disabled={busy} aria-label="Eintrag bearbeiten">
            <IconPencil size={14} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
}
